// ============================================================================
// LE REPÈRE DE TÊTE — rendu DOM de la rangée de tuiles chiffrées qui ouvre un écran de liste.
//
// Extrait à la règle de 2 (largement dépassée : SEPT copies identiques dans le back-office). Anatomie
// et tons en données (`ui-tokens/kpi`) ; ce fichier ne fait que les poser.
//
// ⚠️ LE TON EST UN NOM, PAS UNE COULEUR. Les sept copies passaient des chaînes brutes du genre
// `'var(--teal-100)'` — un hex de repli recopié à la main, donc sept endroits où corriger une
// teinte de charte. L'appelant nomme désormais une famille ; la charte décide de la teinte.
//
// ⚠️ UNE VALEUR ABSENTE SE DIT, ELLE NE SE CHIFFRE PAS (§23.0). `soon` rend « à venir » en encre
// effacée : un `0` serait vrai par accident et faux par intention (« je n'en ai pas encore »), et un
// tiret muet se lirait comme une mesure à zéro. Le POURQUOI va dans `sub`, lu au survol.
// ============================================================================
import type { CSSProperties, ReactNode } from 'react';
import { KPI_TILE, KPI_TILE_TONES, type KpiTone } from '@orgazone/ui-tokens';

export interface KpiItem {
  /** L'étiquette, en capitales — ce qu'on compte. */
  label: string;
  /**
   * La valeur, DÉJÀ FORMATÉE par l'appelant (séparateurs de milliers, unité, monnaie).
   * ⚠️ `null` = pas mesurable aujourd'hui → « à venir ». Ce n'est pas « zéro ».
   */
  value: string | null;
  /** La règle du chiffre, ou le motif de son absence. Rendu au survol — jamais du meublage. */
  sub?: string;
  /** Le tracé SVG de l'icône (`d`), relevé de la maquette. */
  icon: string;
  tone?: KpiTone;
}

export interface KpiRowProps {
  items: KpiItem[];
  style?: CSSProperties;
}

/** Une tuile : carré d'icône teinté, étiquette en capitales, valeur en chiffres tabulaires. */
export function KpiTile({ label, value, sub, icon, tone = 'ink' }: KpiItem) {
  const t = KPI_TILE;
  const c = KPI_TILE_TONES[tone];
  return (
    <div
      title={sub}
      style={{
        display: 'flex', alignItems: 'center', gap: t.gap, padding: t.padding,
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
        background: 'var(--paper)', minWidth: 0,
      }}
    >
      <span style={{
        width: t.square, height: t.square, borderRadius: 'var(--r-ctrl)',
        background: `var(--${c.bg})`, color: `var(--${c.fg})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <svg width={t.iconSize} height={t.iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth={t.iconStroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d={icon} />
        </svg>
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{
          display: 'block', fontSize: t.labelSize, fontWeight: t.labelWeight,
          letterSpacing: t.labelSpacing, textTransform: 'uppercase', color: 'var(--ink-500)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{label}</span>
        <span style={{
          display: 'block', fontSize: t.valueSize, marginTop: 1,
          fontWeight: value === null ? t.soonWeight : t.valueWeight,
          color: value === null ? 'var(--ink-400)' : 'var(--ink-900)',
          fontStyle: value === null ? 'italic' : 'normal',
          fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{value ?? 'à venir'}</span>
      </span>
    </div>
  );
}

/**
 * La rangée — autant de tuiles que la largeur en porte.
 * ⚠️ Rien n'est rendu sur une liste vide : une grille sans enfant laisserait une marge basse qui
 * décollerait la carte suivante sans raison visible.
 */
export function KpiRow({ items, style }: KpiRowProps): ReactNode {
  if (!items.length) return null;
  const t = KPI_TILE;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${t.gridMin}px, 1fr))`,
      gap: t.gridGap, marginBottom: t.rowGap, ...style,
    }}>
      {items.map((k) => <KpiTile key={k.label} {...k} />)}
    </div>
  );
}
